import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/auth/auth.service';
import { DevUser, DevUserGroup } from '../../core/auth/auth.types';
import { EntityService } from '../../core/entity/entity.service';
import { ThemeService } from '../../core/theme/theme.service';
import { IconComponent } from '../icon/icon';

interface NavLink {
  label: string;
  path: string;
}

/** A heading-plus-users bucket for the Debug "Switch User" menu. */
interface DevUserGroupView {
  group: DevUserGroup;
  heading: string;
  users: DevUser[];
}

/** Display order and headings for the Switch User groups. */
const DEV_USER_GROUPS: readonly { group: DevUserGroup; heading: string }[] = [
  { group: 'client', heading: 'Clients' },
  { group: 'staff', heading: 'CapX staff' },
  { group: 'admin', heading: 'Admin' },
];

@Component({
  selector: 'app-header',
  imports: [RouterLink, RouterLinkActive, MatButtonModule, MatTooltipModule, IconComponent],
  templateUrl: './header.html',
  styleUrl: './header.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  private readonly authService = inject(AuthService);
  private readonly entityService = inject(EntityService);
  private readonly themeService = inject(ThemeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  protected readonly isAuthenticated = this.authService.isAuthenticated;
  protected readonly isMenuOpen = signal(false);
  protected readonly isDark = this.themeService.isDark;

  /** The signed-in user, for the account menu greeting. */
  protected readonly currentUser = this.authService.currentUser;

  /** Best display name for the account menu: full name, else the email. */
  protected readonly displayName = computed(() => {
    const u = this.currentUser();
    if (!u) return '';
    const full = `${u.first_name} ${u.last_name}`.trim();
    return full || u.email;
  });

  /** Whether the account (person) dropdown is open in the desktop bar. */
  protected readonly isUserMenuOpen = signal(false);

  /** Role flags driving which nav groups render — sourced from cached contexts. */
  protected readonly isOps = this.entityService.isOps;
  protected readonly isAdmin = this.entityService.isAdmin;
  protected readonly isAgent = this.entityService.isAgent;
  protected readonly isAgencyAdmin = this.entityService.isAgencyAdmin;

  /** Which role dropdown is open in the desktop bar, if any. */
  protected readonly openMenu = signal<'agent' | 'ops' | 'admin' | null>(null);

  /** Always-visible primary links for any authenticated user. */
  protected readonly navLinks: NavLink[] = [{ label: 'Dashboard', path: '/dashboard' }];

  /**
   * Agent section links (rendered only for agents). "Add a client" and "Team"
   * are gated: the Team page is agency-admin-only (individual agents have no
   * team to manage), so its link is dropped unless {@link isAgencyAdmin}.
   */
  protected readonly agentLinks = computed<NavLink[]>(() => {
    const links: NavLink[] = [
      { label: 'Dashboard', path: '/agent/dashboard' },
      { label: 'Add a client', path: '/agent/clients/new' },
    ];
    if (this.isAgencyAdmin()) {
      links.push({ label: 'Team', path: '/agent/team' });
    }
    return links;
  });

  /** Ops section links (rendered only for ops staff). */
  protected readonly opsLinks: NavLink[] = [
    { label: 'Dashboard', path: '/ops/dashboard' },
    { label: 'Pre-applications', path: '/ops/pre-applications' },
    { label: 'Applications', path: '/ops/applications' },
    { label: 'Debtors', path: '/ops/debtors' },
    { label: 'Invoices', path: '/ops/df/invoices' },
  ];

  /** Admin section links (rendered only for system admins). */
  protected readonly adminLinks: NavLink[] = [
    { label: 'Admin dashboard', path: '/admin/dashboard' },
    { label: 'System status', path: '/admin/system-status' },
    { label: 'System logs', path: '/admin/system-status/logs' },
    { label: 'Database browser', path: '/admin/system-status/database' },
    { label: 'Developer docs', path: '/admin/developer-docs' },
  ];

  // --- Debug menu (local development + staging) ---
  protected readonly isDebugEnabled = environment.debug;
  protected readonly isDebugOpen = signal(false);
  protected readonly devUsers = signal<DevUser[]>([]);
  protected readonly devUsersLoading = signal(false);
  /** The fetch failed — distinct from "it succeeded and the roster is empty". */
  protected readonly devUsersError = signal(false);
  private devUsersLoaded = false;

  /**
   * Dev users bucketed into ordered groups (Clients, CapX staff, Admin) for the
   * Switch User menu. Empty groups are dropped so no stray heading shows.
   */
  protected readonly devUserGroups = computed<DevUserGroupView[]>(() => {
    const users = this.devUsers();
    return DEV_USER_GROUPS.map(({ group, heading }) => ({
      group,
      heading,
      users: users.filter((u) => u.group === group),
    })).filter((g) => g.users.length > 0);
  });

  constructor() {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.closeMenu());

    // Keep cached contexts in step with auth state: load on sign-in (drives the
    // role-aware nav), drop on sign-out so links don't leak across users.
    // Gate on a completed profile — /api/me/contexts/ is ProfileCompleteRequired,
    // so fetching it before the user finishes complete-profile returns a 403.
    // currentUser() is a signal, so this re-fires once profile_completed_at flips.
    effect(() => {
      if (this.isAuthenticated() && this.currentUser()?.profile_completed_at) {
        this.entityService.loadContexts();
      } else {
        this.entityService.clearContexts();
      }
    });
  }

  toggleMenu(): void {
    this.isMenuOpen.update((open) => !open);
    if (this.isMenuOpen() && this.isDebugEnabled) {
      this.loadDevUsers();
    }
  }

  closeMenu(): void {
    this.isMenuOpen.set(false);
    this.isDebugOpen.set(false);
    this.openMenu.set(null);
    this.isUserMenuOpen.set(false);
  }

  /** Toggle the desktop account (person) dropdown. */
  toggleUserMenu(): void {
    this.isUserMenuOpen.update((open) => !open);
  }

  /** Toggle a desktop role dropdown, closing any other that was open. */
  toggleRoleMenu(menu: 'agent' | 'ops' | 'admin'): void {
    this.openMenu.update((current) => (current === menu ? null : menu));
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  logout(): void {
    this.authService.logout().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  toggleDebug(): void {
    this.isDebugOpen.update((open) => !open);
    if (this.isDebugOpen()) {
      this.loadDevUsers();
    }
  }

  /**
   * Fetch the switchable-user roster, once per successful load.
   *
   * The latch is set on SUCCESS only. Setting it up-front meant a failed fetch —
   * or one that ran before the environment had been seeded — left the menu empty
   * for the rest of the page load, with no way to retry short of a reload.
   */
  private loadDevUsers(): void {
    if (this.devUsersLoaded || this.devUsersLoading()) return;
    this.devUsersLoading.set(true);
    this.devUsersError.set(false);
    this.authService
      .devListUsers()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (users) => {
          this.devUsersLoaded = true;
          this.devUsers.set(users);
          this.devUsersLoading.set(false);
        },
        error: () => {
          // Left unlatched deliberately: reopening the menu retries.
          this.devUsersError.set(true);
          this.devUsersLoading.set(false);
        },
      });
  }

  switchUser(email: string): void {
    this.authService
      .devSwitchUser(email)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        this.closeMenu();
        // Refresh the shared context cache for the new user. Without this, any
        // already-mounted view bound to it (e.g. the dashboard) keeps showing
        // the previous user's data until a manual reload, because navigating to
        // a route you're already on is a router no-op and won't re-run ngOnInit.
        // Only fetch once the profile is complete — /api/me/contexts/ 403s
        // otherwise (ProfileCompleteRequired); the header effect picks it up
        // after complete-profile flips profile_completed_at.
        if (user.profile_completed_at) {
          this.entityService.loadContexts();
        } else {
          this.entityService.clearContexts();
        }
        const path = user.profile_completed_at ? '/dashboard' : '/auth/complete-profile';
        void this.router.navigate([path]);
      });
  }
}
